import { useState, useMemo, useCallback } from 'react';
import SearchBar from './components/SearchBar';
import FilterSidebar from './components/FilterSidebar';
import FilterChips from './components/FilterChips';
import SearchResults from './components/SearchResults';
import { searchRadioStations } from '@/mocks/searchRadios';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [selectedLanguage, setSelectedLanguage] = useState('Todos');
  const [selectedCountry, setSelectedCountry] = useState('Todos');
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const normalizedQuery = query.toLowerCase().trim();

  const results = useMemo(() => {
    let filtered = [...searchRadioStations];

    if (normalizedQuery) {
      filtered = filtered.filter((r) => {
        const searchText = [
          r.name,
          r.frequency,
          r.city,
          r.state,
          r.country,
          r.category,
          r.language,
          ...r.announcers,
        ].join(' ').toLowerCase();
        return searchText.includes(normalizedQuery);
      });
    }

    if (activeFilters.includes('live')) {
      filtered = filtered.filter((r) => r.isLive);
    }

    if (activeFilters.includes('mostListened')) {
      filtered.sort((a, b) => b.listeners - a.listeners);
    }

    const categoryFilters = ['Cristiana', 'Regional', 'Noticias', 'Deportes', 'Comunidad'];
    const activeCategoryFilters = activeFilters.filter((f) => categoryFilters.includes(f));
    if (activeCategoryFilters.length > 0) {
      filtered = filtered.filter((r) => {
        const cat = r.category;
        if (activeCategoryFilters.includes('Regional') && (cat === 'Regional' || cat === 'Regional Mexicana')) return true;
        if (activeCategoryFilters.includes('Comunidad') && cat === 'Comunitaria') return true;
        return activeCategoryFilters.includes(cat);
      });
    }

    if (selectedCategory !== 'Todas') {
      if (selectedCategory === 'Regional') {
        filtered = filtered.filter((r) => r.category === 'Regional' || r.category === 'Regional Mexicana');
      } else if (selectedCategory === 'Comunidad') {
        filtered = filtered.filter((r) => r.category === 'Comunitaria');
      } else {
        filtered = filtered.filter((r) => r.category === selectedCategory);
      }
    }

    if (selectedLanguage !== 'Todos') {
      filtered = filtered.filter((r) => r.language === selectedLanguage);
    }

    if (selectedCountry !== 'Todos') {
      filtered = filtered.filter((r) => r.country === selectedCountry);
    }

    return filtered;
  }, [query, activeFilters, selectedCategory, selectedLanguage, selectedCountry]);

  const toggleFilter = useCallback((key: string) => {
    setActiveFilters((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSelectedCategory('Todas');
    setSelectedLanguage('Todos');
    setSelectedCountry('Todos');
  }, []);

  const hasFilters = activeFilters.length > 0 || selectedCategory !== 'Todas' || selectedLanguage !== 'Todos' || selectedCountry !== 'Todos';

  const openMobileSearch = () => setMobileExpanded(true);
  const closeMobileSearch = () => setMobileExpanded(false);

  return (
    <main className="min-h-screen bg-background-50">
      {/* Mobile: collapsed trigger */}
      <div className="lg:hidden">
        {!mobileExpanded ? (
          <div className="px-4 py-3">
            <button
              onClick={openMobileSearch}
              className="w-full flex items-center gap-3 px-4 h-12 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-lg text-foreground-400"></i>
              <span className="text-sm text-foreground-400">Buscar radios...</span>
            </button>
          </div>
        ) : (
          <div className="fixed inset-0 z-50 bg-background-50 flex flex-col">
            <div className="px-4 pt-3 pb-2">
              <SearchBar
                value={query}
                onChange={setQuery}
                onClear={() => setQuery('')}
                resultsCount={results.length}
                totalCount={searchRadioStations.length}
                isMobile
                onBack={closeMobileSearch}
              />
            </div>
            <div className="px-4 pb-2">
              <FilterChips activeFilters={activeFilters} onToggleFilter={toggleFilter} />
            </div>
            {hasFilters && (
              <div className="px-4 pb-2 flex items-center gap-2">
                <span className="text-xs text-foreground-500">{results.length} resultados</span>
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-primary-600 font-medium cursor-pointer hover:text-primary-700"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-4 pb-6">
              <SearchResults
                results={results}
                query={query}
                hasFilters={hasFilters}
                onClearAll={clearAllFilters}
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop: full layout */}
      <div className="hidden lg:block">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          <div className="pt-8 pb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-search-line text-lg text-background-50"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground-950">Buscador Global de Radios</h1>
                <p className="text-sm text-foreground-500">Encuentra tu estación favorita entre {searchRadioStations.length} radios</p>
              </div>
            </div>
            <SearchBar
              value={query}
              onChange={setQuery}
              onClear={() => setQuery('')}
              resultsCount={results.length}
              totalCount={searchRadioStations.length}
            />
          </div>


          <div className="flex gap-6 items-start">
            <FilterSidebar
              activeFilters={activeFilters}
              selectedCategory={selectedCategory}
              selectedLanguage={selectedLanguage}
              selectedCountry={selectedCountry}
              onToggleFilter={toggleFilter}
              onCategoryChange={setSelectedCategory}
              onLanguageChange={setSelectedLanguage}
              onCountryChange={setSelectedCountry}
              onClearAll={clearAllFilters}
            />

            <div className="flex-1 min-w-0 pb-10">
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground-700">
                  {results.length} radio{results.length !== 1 ? 's' : ''} encontrada{results.length !== 1 ? 's' : ''}
                </span>
                {hasFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-close-circle-line"></i>
                    Limpiar filtros
                  </button>
                )}
              </div>
              <SearchResults
                results={results}
                query={query}
                hasFilters={hasFilters}
                onClearAll={clearAllFilters}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: results outside the fullscreen (for non-expanded state) */}
      <div className="lg:hidden">
        {!mobileExpanded && (
          <div className="px-4 pb-8">
            <div className="mb-3">
              <FilterChips activeFilters={activeFilters} onToggleFilter={toggleFilter} />
            </div>
            {hasFilters && (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs text-foreground-500">{results.length} resultados</span>
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-primary-600 font-medium cursor-pointer hover:text-primary-700"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
            <SearchResults
              results={results}
              query={query}
              hasFilters={hasFilters}
              onClearAll={clearAllFilters}
            />
          </div>
        )}
      </div>
    </main>
  );
}